import type Owner from '@ember/owner';
import { setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import type { TableLayoutSettings } from 'lumen/components/hotel/budget-forecast/table/layout-settings';
import {
  BUDGET_EDIT_DATA_TYPE_ORDER,
  BUDGET_VIEW_DATA_TYPE_ORDER,
  type DataType,
  FORECAST_EDIT_DATA_TYPE_ORDER,
  FORECAST_VIEW_DATA_TYPE_ORDER,
} from 'lumen/utils/constants/budget-forecast-constants';

type ViewType = 'budget' | 'forecast';
type ModeType = 'view' | 'edit';

const LOCAL_STORAGE_KEYS = {
  budget: {
    view: 'budget_view_table_layout_settings',
    edit: 'budget_edit_table_layout_settings',
  },
  forecast: {
    view: 'forecast_view_table_layout_settings',
    edit: 'forecast_edit_table_layout_settings',
  },
} as const;

const DATA_TYPE_ORDERS = {
  budget: {
    view: BUDGET_VIEW_DATA_TYPE_ORDER,
    edit: BUDGET_EDIT_DATA_TYPE_ORDER,
  },
  forecast: {
    view: FORECAST_VIEW_DATA_TYPE_ORDER,
    edit: FORECAST_EDIT_DATA_TYPE_ORDER,
  },
} as const;

export class LayoutSettingsHandler {
  @service declare localStorage: Services['local-storage'];

  constructor(owner: Owner) {
    setOwner(this, owner);
  }

  #getDefaultLayoutSettings(options: {
    availableDataTypes: DataType[];
    mode: ModeType;
    view: ViewType;
  }): TableLayoutSettings {
    const { availableDataTypes, view, mode } = options;

    const dataTypeOrder = DATA_TYPE_ORDERS[view][mode];
    const sortedDataTypes = availableDataTypes.toSorted(
      (a, b) => dataTypeOrder.indexOf(a) - dataTypeOrder.indexOf(b),
    );

    if (mode === 'view') {
      return {
        dataTypesOrder: sortedDataTypes,
        hiddenDataTypes: [],
        unpinnedDataTypes: sortedDataTypes,
        pinnedDataTypes: [],
      };
    }

    return {
      dataTypesOrder: sortedDataTypes,
      hiddenDataTypes: [],
    };
  }

  getLayoutSettings(options: {
    availableDataTypes: DataType[];
    mode: ModeType;
    view: ViewType;
  }): TableLayoutSettings {
    const { availableDataTypes, mode, view } = options;
    const localStorageKey = LOCAL_STORAGE_KEYS[view][mode];

    const storedLayoutSettings = this.localStorage.getItem(localStorageKey);

    if (!storedLayoutSettings) {
      return this.#getDefaultLayoutSettings(options);
    }

    return parseStoredLayoutSettings(storedLayoutSettings, availableDataTypes);
  }

  setLayoutSettings(options: {
    layoutSettings: TableLayoutSettings;
    mode: ModeType;
    view: ViewType;
  }): void {
    const { layoutSettings, mode, view } = options;

    const localStorageKey = LOCAL_STORAGE_KEYS[view][mode];
    this.localStorage.setItem(localStorageKey, JSON.stringify(layoutSettings));
  }
}

function isValidDataType(
  value: string,
  validTypes: DataType[],
): value is DataType {
  return validTypes.includes(value as DataType);
}

function filterValidDataTypes(
  dataTypes: string[],
  validTypes: DataType[],
): DataType[] {
  return dataTypes.filter((dataType) => isValidDataType(dataType, validTypes));
}

function deduplicateArray<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

function dropCommonDataTypes(
  pinnedDataTypes: DataType[],
  unpinnedDataTypes: DataType[],
): [DataType[], DataType[]] {
  const pinnedSet = new Set<DataType>(pinnedDataTypes);
  const unpinnedSet = new Set<DataType>(unpinnedDataTypes);

  const commonElements = new Set<DataType>();

  for (const item of pinnedSet) {
    if (unpinnedSet.has(item)) {
      commonElements.add(item);
    }
  }

  const newPinnedDataTypes = pinnedDataTypes.filter(
    (item) => !commonElements.has(item),
  );
  const newUnpinnedDataTypes = unpinnedDataTypes.filter(
    (item) => !commonElements.has(item),
  );

  return [newPinnedDataTypes, newUnpinnedDataTypes] as const;
}

function backfillDataTypes(
  unpinnedDataTypes: DataType[],
  baseDataTypes: DataType[],
): DataType[] {
  const unpinnedSet = new Set<DataType>(unpinnedDataTypes);
  const backfilledDataTypes = baseDataTypes.filter(
    (dataType) => !unpinnedSet.has(dataType),
  );

  return [...unpinnedDataTypes, ...backfilledDataTypes];
}

function parseStoredLayoutSettings(
  layoutSettings: string,
  baseDataTypes: DataType[],
): TableLayoutSettings {
  const parsedSettings = serializeLayoutSettings(layoutSettings);

  const hiddenDataTypes = deduplicateArray(
    filterValidDataTypes(parsedSettings.hiddenDataTypes, baseDataTypes),
  );
  const pinnedDataTypes = deduplicateArray(
    filterValidDataTypes(parsedSettings.pinnedDataTypes ?? [], baseDataTypes),
  );
  const unpinnedDataTypes = deduplicateArray(
    filterValidDataTypes(parsedSettings.unpinnedDataTypes ?? [], baseDataTypes),
  );
  const dataTypesOrder = deduplicateArray(
    filterValidDataTypes(parsedSettings.dataTypesOrder ?? [], baseDataTypes),
  );

  // We need to drop duplicated data types from the pinned and unpinned data types
  // otherwise we will have duplicated data types in the layout settings
  const [filteredPinnedDataTypes, filteredUnpinnedDataTypes] =
    dropCommonDataTypes(pinnedDataTypes, unpinnedDataTypes);

  // If the stored layout settings contain data types that are not in the base data types,
  // e.g. we added new data types, we need to fill unpinned data types
  // with the rest of the missing base data types
  const backfilledUnpinnedDataTypes = backfillDataTypes(
    filteredUnpinnedDataTypes,
    baseDataTypes.filter(
      (dataType) => !filteredPinnedDataTypes.includes(dataType),
    ),
  );

  const backfilledDataTypesOrder = backfillDataTypes(
    dataTypesOrder,
    baseDataTypes,
  );

  return {
    hiddenDataTypes,
    pinnedDataTypes: filteredPinnedDataTypes,
    unpinnedDataTypes: backfilledUnpinnedDataTypes,
    dataTypesOrder: backfilledDataTypesOrder,
  };
}

function serializeLayoutSettings(layoutSettings: string): TableLayoutSettings {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const parsedSettings = JSON.parse(layoutSettings);

  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    hiddenDataTypes: parsedSettings.hiddenDataTypes ?? [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    pinnedDataTypes: parsedSettings.pinnedDataTypes ?? [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    unpinnedDataTypes: parsedSettings.unpinnedDataTypes ?? [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    dataTypesOrder: parsedSettings.dataTypesOrder ?? [],
  };
}
